import { useCallback } from 'react';
import { MapLocation } from '../models/MapModels';
import { GeocodeAddressCommandDto, MysteryShopperClient } from '../api/api.g';
import useApi from './api-hook';
require('../models/helpers/string');

interface GeocodeResult {
  postalCode: string;
  location: string;
  province: string;
  country: string;
  address: string;
}

const GEOCODE_CACHE_TTL_MS = 1000 * 60 * 30;
const GEOCODE_LOCATION_PRECISION = 4;
const geocodeCache = new Map<string, { result: GeocodeResult; expiresAt: number }>();
const geocodeInFlightRequests = new Map<string, Promise<GeocodeResult>>();

const getGeocodeCacheKey = (location: MapLocation): string => {
  const lat = Number(location.lat).toFixed(GEOCODE_LOCATION_PRECISION);
  const lng = Number(location.lng).toFixed(GEOCODE_LOCATION_PRECISION);
  return `${lat}:${lng}`;
};

const useGoogleGeocode = () => {
  const api = useApi(MysteryShopperClient);

  const getGeocode = useCallback(
    (location: MapLocation, callback: (results: GeocodeResult) => void) => {
      if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) {
        return;
      }

      const cacheKey = getGeocodeCacheKey(location);
      const now = Date.now();
      const cachedEntry = geocodeCache.get(cacheKey);
      if (cachedEntry && cachedEntry.expiresAt > now) {
        callback(cachedEntry.result);
        return;
      }

      const inFlightRequest = geocodeInFlightRequests.get(cacheKey);
      if (inFlightRequest) {
        inFlightRequest
          .then((result) => callback(result))
          .catch(() => {
            // Error already logged when the request was created.
          });
        return;
      }

      const viewModel = new GeocodeAddressCommandDto();
      viewModel.latitude = location.lat;
      viewModel.longitude = location.lng;

      const request = api
        .geocodeAddress(viewModel)
        .then((res) => {
          const result: GeocodeResult = {
            postalCode: res.postalCode,
            location: res.locality,
            province: res.province,
            country: res.country,
            address: res.address,
          };

          geocodeCache.set(cacheKey, { result, expiresAt: Date.now() + GEOCODE_CACHE_TTL_MS });
          return result;
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => {
          geocodeInFlightRequests.delete(cacheKey);
        });

      geocodeInFlightRequests.set(cacheKey, request);
      request
        .then((result) => callback(result))
        .catch(() => {
          // Request error already handled above.
        });
    },
    [api],
  );

  return getGeocode;
};

export default useGoogleGeocode;
